"use client";

import { useState } from "react";

import { validateInviteCode } from "@features/invite/validateInviteCode";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import { isValidInviteCodeFormat } from "@utils/invite/inviteCode";
import {
  applyManualInviteCode,
  clearInviteCode,
} from "@utils/invite/inviteCodeCookie.client";
import { inviteCodeState } from "@utils/invite/inviteCodeStore";

/**
 * 邀请码手动输入的**唯一逻辑源** —— 填码弹层（`InviteCodeSheet`）是唯一呈现壳，
 * H5 底部弹层 / PC 居中弹窗只是换壳，判定与落盘口径只有这一份。
 *
 * 原生 KMP 的 `InviteCodeSheet` 是同口径的 Kotlin 实现（无法共享代码，
 * 靠此处注释与同名埋点对齐）。口径见 fe-spec §3.3.3：
 *
 * - 空输入 Apply = 移除当前码，不打网络
 * - 格式非法 / 后端判无效 → 就地报错，原来的码不动
 * - valid / unknown（429、网络失败）→ 放行，手动码只放内存
 */
export type InviteCodeApplyOutcome =
  | { action: "removed" }
  | { action: "applied"; replaced: boolean }
  | { action: "rejected" };

export interface UseInviteCodeApplyOptions {
  /** 每次 Apply 得出结论后回调；removed / applied 时由调用方关闭弹层并上报埋点。 */
  onResult: (outcome: InviteCodeApplyOutcome) => void;
}

export interface UseInviteCodeApplyResult {
  /** 当前输入值 */
  value: string;
  /** 输入变化（会顺带清掉错误态） */
  onValueChange: (next: string) => void;
  /** 是否展示错误态（格式非法 / 后端判无效） */
  error: boolean;
  /** 远程校验进行中 */
  checking: boolean;
  /** 是否允许提交（空值也允许：空值即移除） */
  canSubmit: boolean;
  /** 提交 */
  apply: () => Promise<void>;
  /** 弹层每次打开时调用：预填当前码、清错误 */
  reset: () => void;
}

export function useInviteCodeApply({
  onResult,
}: UseInviteCodeApplyOptions): UseInviteCodeApplyResult {
  const { memberApi } = useOspApiClientProvider();

  // 预填当前生效的码（链接带来的或之前手动填的），用户可改可清空
  const [value, setValue] = useState(() => inviteCodeState.code ?? "");
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);

  const onValueChange = (next: string) => {
    setValue(next);
    // 用户重新输入即清错，避免错误态一直挂着
    if (error) {
      setError(false);
    }
  };

  const reset = () => {
    setValue(inviteCodeState.code ?? "");
    setError(false);
  };

  const apply = async () => {
    if (checking) {
      return;
    }
    const trimmed = value.trim();
    // ① 清空后 Apply = 移除（S2.8），不校验
    if (!trimmed) {
      clearInviteCode();
      onResult({ action: "removed" });
      return;
    }
    // ② 格式兜底：本地就能判死的，不必打网络（也省限流额度）
    if (!isValidInviteCodeFormat(trimmed)) {
      setError(true);
      onResult({ action: "rejected" });
      return;
    }
    // ③ 远程校验：`GET /auth/validate`，与注册链路共用后端 InviteCodeResolver，口径一致
    const replaced = inviteCodeState.code !== null;
    setChecking(true);
    const result = await validateInviteCode(memberApi, trimmed);
    setChecking(false);

    // 用户**主动输入**的码判定为无效 → 就地报错让他当场改，原来的码保留
    if (result.status === "invalid") {
      setError(true);
      onResult({ action: "rejected" });
      return;
    }
    // valid 与 unknown 都放行，不把用户卡住：
    // unknown = 429 限流 / 网络失败，判定不了不等于无效，注册时后端会再判一次。
    applyManualInviteCode(trimmed);
    onResult({ action: "applied", replaced });
  };

  return {
    value,
    onValueChange,
    error,
    checking,
    canSubmit: !checking,
    apply,
    reset,
  };
}
